/**
 * Accordion Section Validation Utilities
 * Validates each accordion section and generates summary text
 */

import type { CreateCommunityFormData } from "@/features/communities/schemas/community.schema"
import type { CommunityUserWithRole, LocationData } from "@/features/communities/types"

export interface SectionValidationResult {
  isComplete: boolean
  summary?: string
  displayName?: string
  error?: string
  userPreviews?: Array<{
    userId: string
    name: string | null
    email: string
    username: string | null
    profilePictureUrl: string | null
  }>
}

/**
 * Validate Basic Information Section
 * Checks for: name, handle
 */
export function validateBasicSection(
  data: CreateCommunityFormData,
  _locationData?: LocationData | null,
): SectionValidationResult {
  const hasName = Boolean(data.name?.trim())
  const hasHandle = Boolean(data.handle?.trim())

  const isComplete = hasName && hasHandle

  // Generate summary text: "@handle"
  let summary: string | undefined
  if (isComplete) {
    summary = `@${data.handle}`
  }

  return {
    isComplete,
    summary,
    displayName: isComplete ? data.name : undefined,
  }
}

/**
 * Validate Location Section
 * Checks for: address or any location data
 */
export function validateLocationSection(
  data: CreateCommunityFormData,
  locationData?: LocationData | null,
): SectionValidationResult {
  const hasAddress = Boolean(data.address?.trim())
  const hasLocationData = Boolean(
    locationData &&
      (Boolean(locationData.city) ||
        Boolean(locationData.state) ||
        Boolean(locationData.country) ||
        Boolean(locationData.zipCode)),
  )

  const isComplete: boolean = hasAddress || hasLocationData

  return {
    isComplete,
    summary: isComplete ? "Location information added" : undefined,
  }
}

/**
 * Validate Media Section
 * Checks for: avatar or cover images
 */
export function validateMediaSection(mediaFiles: {
  images: Array<{ id: string; file?: File; src?: string; isCover: boolean }>
  videos: Array<{ id: string; file?: File; src?: string }>
}): SectionValidationResult {
  const imageCount = mediaFiles.images.length
  const videoCount = mediaFiles.videos.length

  // Cover image is required if any images exist
  const hasCoverImage = mediaFiles.images.some(img => img.isCover)
  const hasCoverRequired = imageCount > 0 && !hasCoverImage

  const isComplete = (imageCount > 0 || videoCount > 0) && !hasCoverRequired

  // Format: "5 photos / 1 video" or "5 photos" or "1 video"
  let summary: string | undefined
  if (imageCount > 0 || videoCount > 0) {
    const parts = []
    if (imageCount > 0) {
      parts.push(`${imageCount} photo${imageCount > 1 ? "s" : ""}`)
    }
    if (videoCount > 0) parts.push(`${videoCount} video${videoCount > 1 ? "s" : ""}`)
    summary = parts.join(" / ")
  }

  return {
    isComplete,
    summary,
  }
}

/**
 * Validate Description & Tags Section
 * Checks for: description
 */
export function validateTagsSection(
  data: CreateCommunityFormData,
  _locationData?: LocationData | null,
): SectionValidationResult {
  const hasDescription = Boolean(data.description?.trim())

  // At minimum, description is required
  const isComplete = hasDescription

  // Create description preview with ellipse - show more text
  let summary: string | undefined
  if (isComplete && data.description) {
    const words = data.description.split(" ")
    const truncated = words.slice(0, 20).join(" ")
    summary = truncated + (words.length > 20 ? "..." : "")
  }

  return {
    isComplete,
    summary,
  }
}

/**
 * Validate Social Links Section
 * Checks for: any social link (website, instagram, facebook, twitter)
 */
export function validateSocialSection(data: CreateCommunityFormData): SectionValidationResult {
  const hasWebsite = Boolean(data.websiteUrl?.trim())
  const hasInstagram = Boolean(data.instagramUrl?.trim())
  const hasFacebook = Boolean(data.facebookUrl?.trim())
  const hasTwitter = Boolean(data.twitterUrl?.trim())

  const socialLinksCount = [hasWebsite, hasInstagram, hasFacebook, hasTwitter].filter(
    Boolean,
  ).length
  const isComplete = socialLinksCount > 0

  return {
    isComplete,
    summary: isComplete
      ? `${socialLinksCount} social link${socialLinksCount > 1 ? "s" : ""} added`
      : undefined,
  }
}

/**
 * Validate Privacy Settings Section
 * Checks for: addToRituals and comment required privacy fields
 */
export function validatePrivacySection(data: CreateCommunityFormData): SectionValidationResult {
  const hasAddToRituals =
    data.privacySettings?.addToRituals && data.privacySettings.addToRituals.length > 0
  const hasComment = data.privacySettings?.comment && data.privacySettings.comment.length > 0

  // Only addToRituals and comment are required
  const isComplete = hasAddToRituals && hasComment

  return {
    isComplete,
    summary: isComplete ? "Privacy settings configured" : undefined,
  }
}

/**
 * Validate Guidelines Section
 * Checks for: guidelines text
 */
export function validateGuidelinesSection(data: CreateCommunityFormData): SectionValidationResult {
  const hasGuidelines = Boolean(data.guidelines?.trim())
  const isComplete = hasGuidelines

  // Create guidelines preview with ellipse - show more text
  let summary: string | undefined
  if (isComplete && data.guidelines) {
    const words = data.guidelines.split(" ")
    const truncated = words.slice(0, 20).join(" ")
    summary = truncated + (words.length > 20 ? "..." : "")
  }

  return {
    isComplete,
    summary,
  }
}

/**
 * Validate Users Section
 * Checks for: at least one community member
 */
export function validateUsersSection(
  _data: CreateCommunityFormData,
  selectedUsers?: CommunityUserWithRole[],
): SectionValidationResult {
  const hasUsers = Array.isArray(selectedUsers) && selectedUsers.length > 0
  const isComplete = hasUsers

  // Return first 3 users for preview
  const userPreviews = hasUsers
    ? selectedUsers.slice(0, 3).map(u => ({
        userId: u.userId,
        name: u.name,
        email: u.email,
        username: u.username,
        profilePictureUrl: u.profilePictureUrl,
      }))
    : []

  return {
    isComplete,
    summary: isComplete
      ? `${selectedUsers.length} member${selectedUsers.length > 1 ? "s" : ""}`
      : undefined,
    userPreviews,
  }
}
